/**
 * 💶 CoachPayoutsTab — Onglet "Paiements" du dashboard coach (premium light)
 *
 * Refonte ivoire/violet/or cohérente avec la charte COACHDIA.
 * Logique métier identique à la version précédente — seul le rendu visuel change.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Download, RefreshCw, FileText, CreditCard, TrendingUp, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import { getApiUrl } from '../../config/environment';
import { useAuth } from '../../contexts/AuthContext';

const T = {
  bg: '#FBF9F3',
  card: '#FFFFFF',
  ink: '#1C1147',
  inkSoft: '#3A2E6E',
  text: '#1F2937',
  muted: '#6B7280',
  border: '#E8E2D2',
  borderSoft: '#F0EAD9',
  gold: '#D4AF37',
  goldDeep: '#B7901F',
  goldLight: '#F3D060',
  ivoryDeep: '#FFFCEC',
  green: '#48825A',
  greenSoft: '#E5F3E8',
  violetSoft: '#F4EFFF',
  violetAccent: '#7A4FCF',
};

const fmtMoney = (n) =>
  new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 2,
  }).format(Number(n || 0));

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('fr-FR');
  } catch {
    return iso.slice(0, 10);
  }
};

const PAYMENT_METHOD_LABEL = {
  virement: 'Virement bancaire',
  paypal: 'PayPal',
};

function StatCard({ icon: Icon, label, value, sub, accent = T.gold, testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-2xl p-4 sm:p-5 transition"
      style={{
        background: T.card,
        border: `1px solid ${T.border}`,
        boxShadow: '0 1px 0 rgba(28,17,71,0.03), 0 8px 22px -16px rgba(28,17,71,0.18)',
      }}
    >
      <div className="flex items-center gap-2 mb-3">
        <span
          className="w-9 h-9 rounded-xl flex items-center justify-center"
          style={{
            background: `linear-gradient(135deg, ${accent}22, ${accent}11)`,
            color: accent,
            border: `1px solid ${accent}33`,
          }}
        >
          <Icon className="w-4 h-4" />
        </span>
        <span
          className="text-[10px] uppercase font-semibold tracking-[0.18em]"
          style={{ color: T.muted }}
        >
          {label}
        </span>
      </div>
      <div
        className="text-2xl sm:text-3xl tabular-nums leading-tight"
        style={{
          color: T.ink,
          fontFamily: 'Georgia, "Times New Roman", serif',
          fontWeight: 600,
          letterSpacing: '-0.01em',
        }}
      >
        {value}
      </div>
      {sub && (
        <div className="mt-1.5 text-xs" style={{ color: T.muted }}>
          {sub}
        </div>
      )}
    </div>
  );
}

export default function CoachPayoutsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const { token } = useAuth();
  const API = getApiUrl();

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(`${API}/coach/payment-receipts/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const json = await r.json();
      setData(json);
    } catch (e) {
      setError(e.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [API, token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const downloadPdf = async (receipt) => {
    if (downloadingId) return;
    setDownloadingId(receipt.receipt_id);
    try {
      const r = await fetch(
        `${API}/coach/payment-receipts/${receipt.receipt_id}/download`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${receipt.receipt_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success('Justificatif téléchargé');
    } catch (e) {
      toast.error(`Téléchargement impossible : ${e.message}`);
    } finally {
      setDownloadingId(null);
    }
  };

  const receipts = data?.receipts || [];

  return (
    <div
      data-testid="coach-payouts-tab"
      className="rounded-2xl p-5 sm:p-7 space-y-6"
      style={{
        background: `radial-gradient(120% 80% at 0% 0%, ${T.ivoryDeep} 0%, ${T.bg} 50%)`,
        border: `1px solid ${T.borderSoft}`,
      }}
    >
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <p
            className="uppercase text-[10px] sm:text-[11px] tracking-[0.22em] mb-1.5 font-semibold"
            style={{ color: T.goldDeep }}
          >
            COACH DIA · Versements
          </p>
          <h2
            className="text-2xl sm:text-[28px] leading-tight"
            style={{
              color: T.ink,
              fontFamily: 'Georgia, "Times New Roman", serif',
              fontWeight: 600,
              letterSpacing: '-0.015em',
            }}
          >
            Mes paiements
          </h2>
          <p className="text-xs sm:text-sm mt-1.5" style={{ color: T.muted }}>
            Justificatifs de versement émis par AMSY E-COM SAS
          </p>
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          data-testid="coach-payouts-refresh-btn"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold transition disabled:opacity-60"
          style={{
            background: T.card,
            color: T.ink,
            border: `1px solid ${T.border}`,
            boxShadow: '0 4px 12px -6px rgba(28,17,71,0.12)',
          }}
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          Actualiser
        </button>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
        <StatCard
          testid="coach-payouts-kpi-total"
          icon={CreditCard}
          label="Total versé"
          value={fmtMoney(data?.total_paid_all_time)}
          sub={`${data?.total || 0} justificatif(s)`}
          accent={T.green}
        />
        <StatCard
          testid="coach-payouts-kpi-30d"
          icon={TrendingUp}
          label="30 derniers jours"
          value={fmtMoney(data?.total_paid_last_30d)}
          accent={T.gold}
        />
        <StatCard
          testid="coach-payouts-kpi-count"
          icon={FileText}
          label="Justificatifs"
          value={data?.total || 0}
          sub="Conservation 10 ans"
          accent={T.violetAccent}
        />
      </div>

      {error && (
        <div
          className="p-4 rounded-xl text-sm"
          style={{
            background: '#FFF4F2',
            border: '1px solid #F8C9C0',
            color: '#9B2C1F',
          }}
        >
          Erreur : {error}
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && receipts.length === 0 && (
        <div
          data-testid="coach-payouts-empty"
          className="rounded-2xl p-10 sm:p-14 text-center"
          style={{
            background: `linear-gradient(135deg, ${T.card} 0%, ${T.ivoryDeep} 100%)`,
            border: `1px dashed ${T.border}`,
          }}
        >
          <div
            className="w-14 h-14 mx-auto rounded-2xl flex items-center justify-center mb-4"
            style={{
              background: `linear-gradient(135deg, ${T.goldLight}33, ${T.gold}22)`,
              border: `1px solid ${T.gold}44`,
            }}
          >
            <CreditCard className="w-6 h-6" style={{ color: T.goldDeep }} />
          </div>
          <p
            className="text-base sm:text-lg mb-1"
            style={{
              color: T.ink,
              fontFamily: 'Georgia, "Times New Roman", serif',
              fontWeight: 600,
            }}
          >
            Aucun justificatif de paiement pour le moment.
          </p>
          <p className="text-xs sm:text-sm" style={{ color: T.muted }}>
            Vos justificatifs apparaîtront ici dès qu'un paiement sera effectué.
          </p>
        </div>
      )}

      {/* Receipts list */}
      {receipts.length > 0 && (
        <div data-testid="coach-payouts-list" className="space-y-2.5">
          {receipts.map((r) => (
            <motion.div
              key={r.receipt_id}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              data-testid={`coach-payout-row-${r.receipt_id}`}
              className="rounded-2xl p-4 sm:p-5 transition"
              style={{
                background: T.card,
                border: `1px solid ${T.border}`,
                boxShadow: '0 1px 0 rgba(28,17,71,0.02), 0 6px 20px -16px rgba(28,17,71,0.15)',
              }}
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                    <span
                      className="text-[11px] font-mono px-2 py-0.5 rounded-md"
                      style={{
                        color: T.goldDeep,
                        background: T.ivoryDeep,
                        border: `1px solid ${T.border}`,
                      }}
                    >
                      {r.receipt_number}
                    </span>
                    <span
                      className="px-2 py-0.5 rounded-md text-[10px] font-semibold"
                      style={{
                        background: T.greenSoft,
                        color: T.green,
                        border: `1px solid ${T.green}33`,
                      }}
                    >
                      ✓ Payé
                    </span>
                  </div>
                  <div
                    className="text-lg sm:text-xl tabular-nums leading-tight"
                    style={{
                      color: T.ink,
                      fontFamily: 'Georgia, "Times New Roman", serif',
                      fontWeight: 600,
                    }}
                  >
                    {fmtMoney(r.amount_paid)}
                  </div>
                  <div
                    className="text-xs mt-1.5 flex flex-wrap gap-x-3 gap-y-1"
                    style={{ color: T.muted }}
                  >
                    <span className="inline-flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {fmtDate(r.paid_at || r.issued_at)}
                    </span>
                    <span>· {r.sessions_count} session(s)</span>
                    <span>
                      · {PAYMENT_METHOD_LABEL[r.payment_method] || r.payment_method}
                    </span>
                    <span>
                      · CA {fmtMoney(r.total_generated)} · Commission{' '}
                      {r.commission_rate_display}%
                    </span>
                  </div>
                  {r.email_sent_at && (
                    <div className="text-[10px] mt-1.5" style={{ color: T.muted }}>
                      ✉︎ Email envoyé le {fmtDate(r.email_sent_at)}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => downloadPdf(r)}
                  disabled={downloadingId === r.receipt_id}
                  data-testid={`coach-payout-download-${r.receipt_id}`}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-xs font-semibold transition disabled:opacity-60 hover:-translate-y-0.5"
                  style={{
                    background: `linear-gradient(135deg, ${T.goldDeep} 0%, ${T.gold} 50%, ${T.goldLight} 100%)`,
                    color: T.ink,
                    boxShadow: `0 8px 22px -10px ${T.gold}AA`,
                  }}
                >
                  <Download className="w-3.5 h-3.5" />
                  {downloadingId === r.receipt_id ? '…' : 'Télécharger'}
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      <p
        className="text-[10px] sm:text-[11px] text-center pt-2"
        style={{ color: T.muted }}
      >
        📌 Documents internes émis par <b style={{ color: T.inkSoft }}>AMSY E-COM SAS</b>{' '}
        dans le cadre de la plateforme <b style={{ color: T.inkSoft }}>COACH DIA</b> · Conservation 10 ans
      </p>
    </div>
  );
}
